// Call-recordings portal — client data layer.
//
// Reads go DIRECTLY against Firestore under the security rules (the canonical
// server-side enforcement point — see firestore.rules). The rules guarantee an
// `agent` can only ever read rows where agent_email == their own token email,
// regardless of what query the client constructs. This module just builds
// well-formed, index-friendly queries on top of that guarantee.
//
// Playback is the one operation that must go through a Cloud Function: minting
// a presigned S3 URL needs AWS credentials and must be audited, so it can't
// happen client-side. See requestPlaybackUrl() + the generateRecordingUrl
// callable in functions/index.js.

import {
  collection,
  endAt,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  startAfter,
  startAt,
  where,
  QueryConstraint,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { app, firestore } from '../firebase';
import {
  DB_ACCESS_AUDIT,
  DB_RECORDINGS,
  FN_GENERATE_RECORDING_URL,
  RECORDINGS_GLOBAL_SEARCH_LIMIT,
  RECORDINGS_PAGE_SIZE,
  RECORDINGS_PAGE_SIZE_MAX,
  RECORDINGS_WORKING_SET_CAP,
} from './constants';
import {
  AccessAudit,
  Recording,
  RecordingFilters,
  RecordingsRole,
  isElevatedRecordingsRole,
} from './types';

// Categorical equality filters we can push to the server, in priority order.
// We push AT MOST ONE (composite-index budget); any others are applied
// client-side to the fetched page. call_id is handled by its own exact path.
const CATEGORICAL_PRIORITY: Array<keyof RecordingFilters> = [
  'campaign',
  'disposition',
  'direction',
  'from_number',
];

function pickServerCategorical(
  filters: RecordingFilters,
): { field: keyof RecordingFilters; value: string } | null {
  for (const field of CATEGORICAL_PRIORITY) {
    const value = filters[field];
    if (typeof value === 'string' && value.length > 0) {
      return { field, value };
    }
  }
  return null;
}

export interface RecordingsPage {
  items: Recording[];
  // Opaque cursor for the next page; undefined when there are no more rows.
  cursor?: QueryDocumentSnapshot<DocumentData>;
}

function snapToRecording(snap: QueryDocumentSnapshot<DocumentData>): Recording {
  const d = snap.data();
  return {
    id: snap.id,
    call_id: d.call_id ?? '',
    agent_email: d.agent_email ?? null,
    s3_bucket: d.s3_bucket ?? '',
    s3_key: d.s3_key ?? '',
    from_number: d.from_number ?? '',
    from_number_raw: d.from_number_raw ?? null,
    direction: d.direction,
    disposition: d.disposition ?? '',
    campaign: d.campaign ?? '',
    recorded_at: d.recorded_at ?? 0,
    expires_at: d.expires_at ?? 0,
    created_at: d.created_at ?? 0,
    deleted_at: d.deleted_at ?? null,
    needs_reconciliation: d.needs_reconciliation ?? false,
  };
}

// Apply the filters we did NOT push to the server, in memory, to the page.
function applyClientFilters(
  items: Recording[],
  filters: RecordingFilters,
  serverField: keyof RecordingFilters | null,
): Recording[] {
  const now = Date.now();
  return items.filter((r) => {
    // Defensive UX filters — hide soft-deleted / lifecycled-away audio so we
    // don't offer a play button that would 404.
    if (r.deleted_at) return false;
    if (r.expires_at && r.expires_at <= now) return false;
    // Hide unresolved rows (agent_email couldn't be parsed) from the list —
    // they're admin-only reconciliation items, surfaced via the health script,
    // not real playable recordings. (Agents never receive them anyway.)
    if (r.needs_reconciliation) return false;

    for (const field of CATEGORICAL_PRIORITY) {
      if (field === serverField) continue; // already enforced server-side
      const want = filters[field];
      if (typeof want === 'string' && want.length > 0) {
        if ((r as any)[field] !== want) return false;
      }
    }
    return true;
  });
}

/**
 * List the caller's scoped recordings, paginated.
 *
 * The mandatory scope predicate (agent_email == own email for agents) is
 * enforced by the SECURITY RULES, not just here — a crafted client can't widen
 * it. Elevated roles (supervisor / qa_lead / admin) read team-wide.
 */
export async function listRecordings(opts: {
  role: RecordingsRole;
  email: string;
  filters?: RecordingFilters;
  pageSize?: number;
  cursor?: QueryDocumentSnapshot<DocumentData>;
}): Promise<RecordingsPage> {
  const { role, email } = opts;
  const filters = opts.filters ?? {};
  const pageSize = Math.min(opts.pageSize ?? RECORDINGS_PAGE_SIZE, RECORDINGS_PAGE_SIZE_MAX);
  const elevated = isElevatedRecordingsRole(role);
  const col = collection(firestore, DB_RECORDINGS);

  // --- call_id exact lookup path ---
  // A near-unique lookup. For agents the query MUST also carry their own
  // agent_email equality: the security rules reject any list query that isn't
  // provably scoped to the caller, so a bare call_id filter would be
  // permission-denied wholesale — even for the agent's own call. Adding the
  // caller's own email never widens anything (equality-only, so no composite
  // index needed either). A call_id that isn't theirs simply returns no rows.
  if (filters.call_id) {
    const constraints: QueryConstraint[] = [
      ...(elevated ? [] : [where('agent_email', '==', email.toLowerCase())]),
      where('call_id', '==', filters.call_id),
      fbLimit(pageSize),
    ];
    if (opts.cursor) constraints.splice(constraints.length - 1, 0, startAfter(opts.cursor));
    try {
      const snap = await getDocs(query(col, ...constraints));
      const items = applyClientFilters(snap.docs.map(snapToRecording), filters, null);
      return { items, cursor: pageCursor(snap.docs, pageSize) };
    } catch (e) {
      if (isPermissionDenied(e)) return { items: [] };
      throw e;
    }
  }

  // --- normal scoped + ordered path ---
  const constraints: QueryConstraint[] = [];
  if (!elevated) constraints.push(where('agent_email', '==', email.toLowerCase()));

  const cat = pickServerCategorical(filters);
  if (cat) constraints.push(where(cat.field as string, '==', cat.value));

  if (typeof filters.date_from === 'number') {
    constraints.push(where('recorded_at', '>=', filters.date_from));
  }
  if (typeof filters.date_to === 'number') {
    constraints.push(where('recorded_at', '<=', filters.date_to));
  }

  constraints.push(orderBy('recorded_at', 'desc'));
  if (opts.cursor) constraints.push(startAfter(opts.cursor));
  constraints.push(fbLimit(pageSize));

  const snap = await getDocs(query(col, ...constraints));
  const items = applyClientFilters(
    snap.docs.map(snapToRecording),
    filters,
    cat ? cat.field : null,
  );
  return { items, cursor: pageCursor(snap.docs, pageSize) };
}

// Load a bounded working set for the caller (scope + optional date range),
// newest first, for LIVE client-side partial-match filtering in the UI.
// Firestore can't do substring/fuzzy search, so the page pulls this set and
// filters it in memory as the user types. Returns `capped: true` when more
// rows exist beyond this chunk — pass the returned `cursor` back in to append
// the next-older chunk ("Load older"). Identifier lookups do NOT depend on
// this window: searchRecordingsGlobal() covers the full scoped history.
export async function loadRecordingsWorkingSet(opts: {
  role: RecordingsRole;
  email: string;
  dateFrom?: number;
  dateTo?: number;
  cap?: number;
  cursor?: QueryDocumentSnapshot<DocumentData>;
}): Promise<{
  items: Recording[];
  capped: boolean;
  cursor?: QueryDocumentSnapshot<DocumentData>;
}> {
  const cap = Math.max(1, opts.cap ?? RECORDINGS_WORKING_SET_CAP);
  const col = collection(firestore, DB_RECORDINGS);
  const constraints: QueryConstraint[] = [];
  if (!isElevatedRecordingsRole(opts.role)) {
    constraints.push(where('agent_email', '==', opts.email.toLowerCase()));
  }
  if (typeof opts.dateFrom === 'number') constraints.push(where('recorded_at', '>=', opts.dateFrom));
  if (typeof opts.dateTo === 'number') constraints.push(where('recorded_at', '<=', opts.dateTo));
  constraints.push(orderBy('recorded_at', 'desc'));
  if (opts.cursor) constraints.push(startAfter(opts.cursor));
  constraints.push(fbLimit(cap + 1)); // +1 sentinel to detect capping

  const snap = await getDocs(query(col, ...constraints));
  const capped = snap.docs.length > cap;
  const page = snap.docs.slice(0, cap);
  const raw = page.map(snapToRecording);
  // Drop deleted / expired / unresolved rows (no categorical filters here).
  const items = applyClientFilters(raw, {}, null);
  return { items, capped, cursor: capped ? page[page.length - 1] : undefined };
}

// Search the caller's FULL scoped history by identifier prefix — this is what
// makes "paste a call ID, get the call" work no matter how old the call is or
// what the browse window has loaded. Firestore can't do substrings, but it can
// do prefix ranges (orderBy(field) + startAt(q) + endAt(q + '\uf8ff')), and
// identifiers are exactly the fields people paste whole or type from the
// start. Agents' queries carry their own agent_email equality so the security
// rules accept them (see listRecordings); elevated roles query unscoped.
// Results from the separate identifier queries are UNIONED here — the page
// applies its normal AND filtering on top, so semantics stay consistent.
export async function searchRecordingsGlobal(opts: {
  role: RecordingsRole;
  email: string;
  callId?: string;
  fromNumber?: string;
  agentQuery?: string; // elevated only — agents are already scoped to themselves
  limitPerQuery?: number;
}): Promise<Recording[]> {
  const elevated = isElevatedRecordingsRole(opts.role);
  const col = collection(firestore, DB_RECORDINGS);
  const max = Math.max(1, opts.limitPerQuery ?? RECORDINGS_GLOBAL_SEARCH_LIMIT);
  const scope: QueryConstraint[] = elevated
    ? []
    : [where('agent_email', '==', opts.email.toLowerCase())];

  const prefix = (field: string, q: string): QueryConstraint[] => [
    ...scope,
    orderBy(field),
    startAt(q),
    endAt(q + '\uf8ff'),
    fbLimit(max),
  ];

  const searches: QueryConstraint[][] = [];

  const callId = (opts.callId ?? '').trim();
  if (callId) searches.push(prefix('call_id', callId));

  const phone = (opts.fromNumber ?? '').trim();
  if (phone) {
    // ANI is stored as it appeared in the filename: optional "+" then digits.
    // People type bare digits, so also try the +/+1-prefixed spellings.
    const variants = new Set([phone]);
    if (/^\d+$/.test(phone)) {
      variants.add('+' + phone);
      variants.add('+1' + phone);
    }
    variants.forEach((v) => searches.push(prefix('from_number', v)));
  }

  const agentQ = (opts.agentQuery ?? '').trim().toLowerCase();
  if (elevated && agentQ) searches.push(prefix('agent_email', agentQ));

  if (searches.length === 0) return [];

  const results = await Promise.all(
    searches.map(async (constraints) => {
      try {
        const snap = await getDocs(query(col, ...constraints));
        return snap.docs.map(snapToRecording);
      } catch (e) {
        // An agent probing an identifier that isn't theirs is a clean
        // "no results", never an error state.
        if (isPermissionDenied(e)) return [];
        throw e;
      }
    }),
  );

  const byId = new Map<string, Recording>();
  results.flat().forEach((r) => byId.set(r.id, r));
  const merged = applyClientFilters(Array.from(byId.values()), {}, null);
  merged.sort((a, b) => b.recorded_at - a.recorded_at);
  return merged;
}

function pageCursor(
  docs: QueryDocumentSnapshot<DocumentData>[],
  pageSize: number,
): QueryDocumentSnapshot<DocumentData> | undefined {
  // Only offer a next-page cursor when the page came back full.
  return docs.length === pageSize ? docs[docs.length - 1] : undefined;
}

function isPermissionDenied(e: unknown): boolean {
  return !!e && typeof e === 'object' && (e as { code?: string }).code === 'permission-denied';
}

// ---------- Playback ----------

export interface PlaybackUrlResult {
  url: string;
  expiresInSeconds: number;
}

// Ask the server to mint a short-lived presigned playback URL. The function
// re-checks authorization and writes an audit row before returning the URL.
export async function requestPlaybackUrl(recordingId: string): Promise<PlaybackUrlResult> {
  const functions = getFunctions(app, 'us-central1');
  const callable = httpsCallable<{ recordingId: string }, PlaybackUrlResult>(
    functions,
    FN_GENERATE_RECORDING_URL,
  );
  const res = await callable({ recordingId });
  return res.data;
}

// ---------- Admin: role management & provisioning ----------
// These wrap admin-only callables (authorized server-side on recordingsRole ==
// 'admin'). See functions/index.js.

export async function setRecordingsRole(
  email: string,
  role: RecordingsRole | null,
): Promise<{ uid: string; email: string; role: RecordingsRole | null }> {
  const functions = getFunctions(app, 'us-central1');
  const callable = httpsCallable(functions, 'setRecordingsRole');
  const res = await callable({ email, role });
  return res.data as { uid: string; email: string; role: RecordingsRole | null };
}

export interface ProvisionResult {
  count: number;
  results: Array<{ email: string; ok: boolean; uid?: string; error?: string }>;
}

export async function provisionRecordingsAgents(
  emails: string[],
  role: RecordingsRole = 'agent',
): Promise<ProvisionResult> {
  const functions = getFunctions(app, 'us-central1');
  const callable = httpsCallable(functions, 'provisionRecordingsAgents');
  const res = await callable({ emails, role });
  return res.data as ProvisionResult;
}

// ---------- Admin: audit log ----------
// Admin-only read (enforced by the security rules). Newest first.
export async function listRecentAudit(max = 100): Promise<AccessAudit[]> {
  const col = collection(firestore, DB_ACCESS_AUDIT);
  const snap = await getDocs(query(col, orderBy('created_at', 'desc'), fbLimit(max)));
  return snap.docs.map((d) => {
    const x = d.data();
    return {
      id: d.id,
      user_id: x.user_id ?? '',
      email: x.email ?? '',
      recording_id: x.recording_id ?? '',
      call_id: x.call_id ?? '',
      action: x.action,
      ip: x.ip ?? '',
      user_agent: x.user_agent ?? '',
      target_agent_email: x.target_agent_email ?? null,
      created_at: x.created_at ?? 0,
    } as AccessAudit;
  });
}
